import React, { useState, useRef, useCallback } from 'react';
import { Mic, Square, Upload, Copy, Check, Loader2, FileAudio, X, Send, Link2, Film } from 'lucide-react';
import { toast } from '../ui/Toast';
import useAudioRecorder from '../../hooks/useAudioRecorder';
import { saveSTTHistory } from '../../services/speechApi';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 Mo
const ACCEPTED_FORMATS = '.mp3,.wav,.m4a,.webm,.ogg,.flac';

const SpeechSTT = () => {
  const [mode, setMode] = useState('record'); // 'record' | 'upload'
  const [language, setLanguage] = useState('fr');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);
  const dropRef = useRef(null);

  // Validation souple, alignée avec le backend /api/voice/reel (H2 souverain).
  const isValidReelUrl = /^https?:\/\//i.test(urlInput) && (
    urlInput.includes('facebook.com') || urlInput.includes('fb.watch') ||
    urlInput.includes('instagram.com') || urlInput.includes('tiktok.com')
  );

  const { isRecording, seconds, audioBlob, error, startRecording, stopRecording, resetRecording, formatTime } = useAudioRecorder();

  // Transcrire un blob audio
  const transcribe = useCallback(async (blob, filename = 'audio.webm', source = 'file') => {
    setProcessing(true);
    setResult(null);
    try {
      const token = localStorage.getItem('oracle_token');
      const formData = new FormData();
      formData.append('file', blob, filename);
      formData.append('language', language);

      const res = await fetch('/api/voice/transcribe', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.details || err.error || `Erreur ${res.status}`);
      }

      const data = await res.json();
      setResult(data.text);
      toast.success('Transcription terminée');
      // Sauvegarder en historique (fire-and-forget)
      saveSTTHistory(data.text, filename, blob.size, language, { source }).catch(() => {});
    } catch (err) {
      toast.error(err.message || 'Erreur transcription');
    } finally {
      setProcessing(false);
    }
  }, [language]);

  // Auto-transcription quand l'enregistrement s'arrête
  const lastBlobRef = useRef(null);
  React.useEffect(() => {
    if (audioBlob && audioBlob !== lastBlobRef.current) {
      lastBlobRef.current = audioBlob;
      console.log('[SpeechSTT] Audio blob:', audioBlob.size, 'bytes, type:', audioBlob.type);
      if (audioBlob.size < 1000) {
        toast.warning('Enregistrement trop court ou silencieux. Réessayez.');
        return;
      }
      transcribe(audioBlob, 'audio.webm', 'mic');
    }
  }, [audioBlob, transcribe]);

  // Upload fichier
  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Fichier trop volumineux (max ${MAX_FILE_SIZE / 1024 / 1024} Mo)`);
      return;
    }
    setUploadFile(file);
  };

  const handleTranscribeFile = () => {
    if (!uploadFile) return;
    transcribe(uploadFile, uploadFile.name, 'file');
  };

  // Transcription depuis une URL Reel/TikTok/IG via H2 souverain (/api/voice/reel)
  const handleTranscribeUrl = async () => {
    const reelUrl = urlInput.trim();
    if (!isValidReelUrl) return;
    setProcessing(true);
    setResult(null);
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch('/api/voice/reel', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: reelUrl }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.details || err.error || `Erreur ${res.status}`);
      }
      const data = await res.json();
      setResult(data.text);
      toast.success('Transcription terminée');
      saveSTTHistory(data.text, reelUrl, 0, language, {
        source: 'reel',
        sourceUrl: reelUrl,
        provider: 'local-h2',
        model: 'whisper-large-v3',
        duration: data.duration,
      }).catch(() => {});
    } catch (err) {
      toast.error(err.message || 'Erreur transcription');
    } finally {
      setProcessing(false);
    }
  };

  // Drag & drop
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); dropRef.current.style.borderColor = '#3b82f6'; };
  const handleDragLeave = (e) => { e.preventDefault(); dropRef.current.style.borderColor = 'var(--card-border)'; };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    dropRef.current.style.borderColor = 'var(--card-border)';
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleCopy = () => {
    if (!result) return;
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
    return `${(bytes / 1024).toFixed(0)} Ko`;
  };

  return (
    <div style={{ display: 'flex', gap: '24px', height: '100%', padding: '24px 0' }}>

      {/* Colonne gauche — Entrée */}
      <div style={{ flex: '0 0 520px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* Toggle mode */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '10px', padding: '3px' }}>
          {[
            { id: 'record', label: 'Enregistrer', icon: Mic },
            { id: 'upload', label: 'Importer un fichier', icon: Upload },
            { id: 'url', label: 'Depuis une URL', icon: Link2 },
          ].map(m => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button key={m.id} onClick={() => { setMode(m.id); setUploadFile(null); setUrlInput(''); resetRecording(); setResult(null); }}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  padding: '9px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: active ? 'rgba(29,78,216,0.15)' : 'transparent',
                  color: active ? '#3b82f6' : 'var(--text-secondary)',
                  fontWeight: active ? 600 : 500, fontSize: '13px', transition: 'all 0.15s',
                }}>
                <Icon size={14} /> {m.label}
              </button>
            );
          })}
        </div>

        {/* Langue */}
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>Langue</label>
          <select value={language} onChange={e => setLanguage(e.target.value)} style={selectStyle}>
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="ar">العربية</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
            <option value="auto">Auto-détection</option>
          </select>
        </div>

        {/* Mode enregistrement */}
        {mode === 'record' && (
          <div style={{
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            borderRadius: '14px', padding: '32px', textAlign: 'center',
          }}>
            {error && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

            <button onClick={isRecording ? stopRecording : startRecording} disabled={processing}
              style={{
                width: 80, height: 80, borderRadius: '50%', border: 'none', cursor: processing ? 'wait' : 'pointer',
                background: isRecording
                  ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
                  : 'linear-gradient(135deg, #1D4ED8, #2563eb)',
                color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: isRecording
                  ? '0 0 20px rgba(220,38,38,0.4)'
                  : '0 4px 14px rgba(29,78,216,0.35)',
                transition: 'all 0.2s',
              }}>
              {isRecording ? <Square size={24} /> : <Mic size={28} />}
            </button>

            <div style={{
              fontSize: '28px', fontFamily: 'monospace', fontWeight: 700, marginTop: '16px',
              color: isRecording ? '#ef4444' : 'var(--text-secondary)',
            }}>
              {formatTime(seconds)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {processing ? 'Transcription en cours...' : isRecording ? 'Cliquez pour arrêter' : 'Cliquez pour enregistrer'}
            </div>

            {isRecording && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '12px' }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} style={{
                    width: 4, height: 12 + Math.random() * 16, borderRadius: 2,
                    background: '#ef4444', opacity: 0.6 + Math.random() * 0.4,
                    animation: `pulse ${0.5 + Math.random() * 0.5}s ease-in-out infinite alternate`,
                  }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Mode upload */}
        {mode === 'upload' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div ref={dropRef}
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                background: 'var(--card-bg)', border: '2px dashed var(--card-border)',
                borderRadius: '14px', padding: '40px 20px', textAlign: 'center',
                cursor: 'pointer', transition: 'border-color 0.2s',
              }}>
              <FileAudio size={32} style={{ color: 'var(--text-secondary)', marginBottom: '10px' }} />
              <div style={{ fontSize: '14px', color: 'var(--text)', fontWeight: 500 }}>
                Glissez un fichier audio ici
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                ou cliquez pour parcourir — MP3, WAV, M4A, WebM, OGG, FLAC (max 25 Mo)
              </div>
              <input ref={fileInputRef} type="file" accept={ACCEPTED_FORMATS} hidden
                onChange={e => handleFileSelect(e.target.files?.[0])} />
            </div>

            {uploadFile && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                borderRadius: '10px', padding: '12px 14px',
              }}>
                <FileAudio size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {uploadFile.name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{formatFileSize(uploadFile.size)}</div>
                </div>
                <button onClick={() => setUploadFile(null)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px' }}>
                  <X size={14} />
                </button>
              </div>
            )}

            <button onClick={handleTranscribeFile} disabled={!uploadFile || processing}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                padding: '14px 24px', borderRadius: '12px', border: 'none',
                cursor: !uploadFile || processing ? 'not-allowed' : 'pointer',
                background: !uploadFile || processing ? 'var(--card-border)' : 'linear-gradient(135deg, #1D4ED8, #2563eb)',
                color: '#fff', fontWeight: 600, fontSize: '15px', width: '100%',
                boxShadow: !uploadFile || processing ? 'none' : '0 4px 18px rgba(29,78,216,0.4)',
                transition: 'all 0.2s',
              }}>
              {processing ? (
                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Transcription...</>
              ) : (
                <><Send size={18} /> Transcrire</>
              )}
            </button>
          </div>
        )}

        {/* Mode URL (Reel / TikTok / Instagram — H2 souverain) */}
        {mode === 'url' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{
              background: 'var(--card-bg)', border: '1px solid var(--card-border)',
              borderRadius: '14px', padding: '20px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Film size={18} style={{ color: '#3b82f6' }} />
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Reel / TikTok / Instagram</span>
              </div>
              <input
                type="text"
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && isValidReelUrl && !processing) handleTranscribeUrl(); }}
                placeholder="https://facebook.com/reel/... ou tiktok.com/@.../video/..."
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: '10px',
                  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                  color: 'var(--text)', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px', lineHeight: 1.5 }}>
                Collez un lien Reel Facebook, TikTok ou Instagram. Transcription souveraine (H2), 1 à 2 min.
              </div>
            </div>

            <button onClick={handleTranscribeUrl} disabled={!isValidReelUrl || processing}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                padding: '14px 24px', borderRadius: '12px', border: 'none',
                cursor: !isValidReelUrl || processing ? 'not-allowed' : 'pointer',
                background: !isValidReelUrl || processing ? 'var(--card-border)' : 'linear-gradient(135deg, #1D4ED8, #2563eb)',
                color: '#fff', fontWeight: 600, fontSize: '15px', width: '100%',
                boxShadow: !isValidReelUrl || processing ? 'none' : '0 4px 18px rgba(29,78,216,0.4)',
                transition: 'all 0.2s',
              }}>
              {processing ? (
                <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Transcription...</>
              ) : (
                <><Send size={18} /> Transcrire</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Colonne droite — Résultat */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {result ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Mic size={16} style={{ color: '#3b82f6' }} />
                Transcription
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={handleCopy} style={{
                  padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--card-border)',
                  background: 'transparent', color: copied ? '#22c55e' : 'var(--text-secondary)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px',
                  transition: 'all 0.15s',
                }}>
                  {copied ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier</>}
                </button>
              </div>
            </div>

            <div style={{
              flex: 1, background: 'var(--card-bg)', border: '1px solid var(--card-border)',
              borderRadius: '14px', padding: '20px', overflow: 'auto',
              fontSize: '14px', lineHeight: 1.7, color: 'var(--text)', whiteSpace: 'pre-wrap',
            }}>
              {result}
            </div>

            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textAlign: 'right' }}>
              {result.length} caractères — {mode === 'url' ? 'Whisper souverain (H2)' : 'OpenAI Whisper'}
            </div>
          </div>
        ) : processing ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px' }}>
            <Loader2 size={36} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Transcription en cours...</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.4, gap: '12px' }}>
            <Mic size={48} style={{ color: 'var(--text-secondary)' }} />
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              Enregistrez, importez un fichier ou collez une URL Reel/TikTok pour transcrire
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { from { transform: scaleY(0.5); } to { transform: scaleY(1.5); } }
      `}</style>
    </div>
  );
};

const selectStyle = {
  width: '100%', padding: '10px 14px', borderRadius: '10px',
  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
  color: 'var(--text)', fontSize: '14px', outline: 'none',
  boxSizing: 'border-box', cursor: 'pointer', appearance: 'auto',
};

export default SpeechSTT;
